import { useContext, useEffect, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { FirebaseContext } from '@/session/FirebaseContext';
import { type LeagueHistoryColumn, queryLeagueHistory } from '@/models/leagueHistory';

export interface UseLeagueHistoryResult {
  columns: LeagueHistoryColumn[];
  loading: boolean;
  error: Error | null;
}

/**
 * One-shot fetch of a league's settled standings history (leagues/{id}/history/*).
 *
 * History columns are immutable once a matchday is settled, so a live subscription
 * is unnecessary — a single getDocs keeps reads cheap.
 */
export function useLeagueHistory(leagueId: string | undefined): UseLeagueHistoryResult {
  const ctx = useContext(FirebaseContext);
  const [columns, setColumns] = useState<LeagueHistoryColumn[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(leagueId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!leagueId) {
      setColumns([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getDocs(queryLeagueHistory(ctx.db, leagueId))
      .then((snapshot) => {
        if (cancelled) return;
        setColumns(snapshot.docs.map((doc) => doc.data() as LeagueHistoryColumn));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[useLeagueHistory]', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ctx.db, leagueId]);

  return { columns, loading, error };
}
